import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_LABELS,
  type SupportedLanguage,
} from '@/i18n';

/**
 * Language picker — persists the choice in the `pc_lang` cookie via the
 * i18next-browser-languagedetector caches: ['cookie'] config in src/i18n.
 */
export function LanguageSwitcher() {
  const { i18n, t } = useTranslation('nav');
  const current = (i18n.resolvedLanguage || i18n.language) as SupportedLanguage;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-md border border-border/60 bg-card/60 px-2.5 text-xs font-medium text-muted-foreground transition-colors',
          'hover:border-primary/40 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        aria-label={t('language')}
      >
        <Globe className="h-3.5 w-3.5" />
        <span className="uppercase">{current}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('language')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isActive = lang === current;
          return (
            <DropdownMenuItem
              key={lang}
              onSelect={() => void i18n.changeLanguage(lang)}
              className="flex items-center justify-between gap-3"
            >
              <span>{LANGUAGE_LABELS[lang]}</span>
              {isActive ? (
                <Check className="h-3.5 w-3.5 text-primary" />
              ) : (
                <span className="font-mono text-[10px] uppercase text-muted-foreground">
                  {lang}
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
